import React, { useEffect } from 'react';
import {
  FaTimes,
  FaShoppingBag,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
  FaSpinner,
  FaSearch,
  FaFilter,
  FaTruck,
} from 'react-icons/fa';
import { Order, OrderStatusLabels, OrderStatusColors } from '../../types/order';
import { LoadingSpinner } from '../ui';

interface UserOrdersModalProps {
  isOpen: boolean;
  onClose: () => void;
  orders: Order[];
  loading: boolean;
  error: string | null;
  onLoadOrders: () => void;
}

const UserOrdersModal: React.FC<UserOrdersModalProps> = ({
  isOpen,
  onClose,
  orders,
  loading,
  error,
  onLoadOrders,
}) => {
  const [filteredOrders, setFilteredOrders] = React.useState<Order[]>(orders);
  const [statusFilter, setStatusFilter] = React.useState<string>('all');
  const [searchTerm, setSearchTerm] = React.useState('');

  useEffect(() => {
    if (isOpen && orders.length === 0 && !loading) {
      onLoadOrders();
    }
  }, [isOpen, orders.length, loading, onLoadOrders]);

  useEffect(() => {
    let filtered = orders;

    // Durum filtresi
    if (statusFilter !== 'all') {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    // Arama filtresi
    if (searchTerm) {
      filtered = filtered.filter(
        order =>
          order.id.toString().includes(searchTerm) ||
          order.items_summary?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredOrders(filtered);
  }, [orders, statusFilter, searchTerm]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatPrice = (price: number | string) => {
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `${(numPrice || 0).toFixed(2)} ₺`;
  };

  const getStatusIcon = (status: Order['status']) => {
    switch (status) {
      case 'pending':
        return <FaClock className="text-yellow-600" />;
      case 'preparing':
        return <FaSpinner className="text-blue-600 animate-spin" />;
      case 'on-the-way':
        return <FaTruck className="text-purple-600" />;
      case 'completed':
        return <FaCheckCircle className="text-green-600" />;
      case 'cancelled':
        return <FaTimesCircle className="text-red-600" />;
      default:
        return <FaClock className="text-gray-600" />;
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="absolute inset-0" onClick={onClose} aria-label="Kapat" />
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden relative">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <FaShoppingBag className="text-green-600 text-xl" />
            <h2 className="text-xl font-semibold text-gray-800">Siparişlerim</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition">
            <FaTimes size={20} />
          </button>
        </div>

        {/* Filtreler */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Arama */}
            <div className="flex-1 relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Sipariş no veya ürün adıyla ara..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            {/* Durum Filtresi */}
            <div className="relative">
              <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="all">Tüm Durumlar</option>
                <option value="pending">Beklemede</option>
                <option value="preparing">Hazırlanıyor</option>
                <option value="on-the-way">Yolda</option>
                <option value="completed">Tamamlandı</option>
                <option value="cancelled">İptal Edildi</option>
              </select>
            </div>
          </div>
        </div>

        {/* İçerik */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <FaTimesCircle className="mx-auto text-4xl text-red-500 mb-4" />
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={onLoadOrders}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                Tekrar Dene
              </button>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="text-center py-12">
              <FaShoppingBag className="mx-auto text-4xl text-gray-400 mb-4" />
              <p className="text-gray-600">
                {searchTerm || statusFilter !== 'all'
                  ? 'Arama kriterlerinize uygun sipariş bulunamadı'
                  : 'Henüz sipariş vermemişsiniz'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredOrders.map(order => (
                <div
                  key={order.id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(order.status)}
                      <div>
                        <h3 className="font-semibold text-gray-800">Sipariş #{order.id}</h3>
                        <p className="text-sm text-gray-600">{formatDate(order.created_at)}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-800 mb-1">
                        {formatPrice(order.total_amount || 0)}
                      </div>
                      <span
                        className={`text-xs px-2 py-1 rounded-full ${OrderStatusColors[order.status]}`}
                      >
                        {OrderStatusLabels[order.status]}
                      </span>
                    </div>
                  </div>

                  {/* Sipariş Detayları */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Birim/Ofis:</span>
                        <span className="ml-2 text-gray-600">
                          {order.management} - {order.office}
                        </span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Kullanılan Fiş:</span>
                        <span className="ml-2 text-gray-600">{order.ticket_used} adet</span>
                      </div>
                    </div>
                    {order.items_summary && (
                      <div className="mt-2">
                        <span className="font-medium text-gray-700 text-sm">Ürünler:</span>
                        <p className="text-sm text-gray-600 mt-1">{order.items_summary}</p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="text-center text-sm text-gray-600">
            Toplam {orders.length} sipariş
            {filteredOrders.length !== orders.length && ` (${filteredOrders.length} gösteriliyor)`}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserOrdersModal;
